import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModerationQueue } from "hooks/useModeration";
import userServices from "services/userServices";
import Pagination from "views/pages/components/Pagination";
import { Avatar } from "@mui/material";
import constants from "constants";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import SimpleHeader from "components/Headers/SimpleHeader";
import DeleteModal from "../../../modals/DeleteModal";
import {
  Card,
  Container,
  Input,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Spinner,
  Alert,
  Table
} from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChatIcon from "@mui/icons-material/Chat";
import useNotificationHighlight from "hooks/useNotificationHighlight";

function VerificationQueue() {
  const { t } = useTranslation();
  const { isHighlighted } = useNotificationHighlight();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const {
    data,
    totalItems,
    loading,
    error,
    page,
    limit,
    search,
    setSearch,
    sortField,
    sortOrder,
    handlePageChange,
    handleLimitChange,
    handleSort,
    refresh
  } = useModerationQueue("verification");

  const [selectedFlag, setSelectedFlag] = useState(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const toggleApproveModal = (flag = null) => {
    setSelectedFlag(flag);
    setApproveModalOpen(prev => !prev);
  };

  const toggleMessageModal = (flag = null) => {
    setSelectedFlag(flag);
    setMessageText("");
    setMessageModalOpen(prev => !prev);
  };

  // 1. Handle Verification Approval
  const handleApprove = async () => {
    if (!selectedFlag) return;
    try {
      setActionLoading(true);
      const res = await userServices.saveDecision(selectedFlag._id, "dismiss", t("categories.moderation.verification.approveNotes", "Verification approved by admin."));
      if (res && res.success) {
        notify("success", t("page.flagged.toast.statusUpdated", "Verification approved successfully"));
        window.dispatchEvent(new Event("moderationAction"));
        refresh();
        toggleApproveModal();
      } else {
        notify("error", res.error || t("page.flagged.toast.statusUpdateFailed", "Verification approval failed"));
      }
    } catch (err) {
      notify("error", t("page.flagged.toast.genericError", "Failed to approve verification"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (messageText.trim().length === 0) return;
    try {
      setActionLoading(true);
      if (!selectedFlag || !selectedFlag._id) {
        notify("error", t("categories.moderation.verification.noFlagId", "Target flag ID not found"));
        return;
      }
      
      const res = await userServices.saveGuidance(selectedFlag._id, messageText.trim());
      if (res && res.success) {
        notify("success", t("categories.moderation.verification.messageSent", "Message sent successfully"));
        toggleMessageModal();
        refresh();
      } else {
        notify("error", res?.error || t("page.flagged.toast.statusUpdateFailed", "Failed to save suggestion"));
      }
    } catch (err) {
      console.error(err);
      notify("error", t("page.flagged.toast.genericError", "Failed to send message"));
    } finally {
      setActionLoading(false);
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <span className="order-4" />;
    }
    return sortOrder === "asc" ? <span className="caret-4-asc" /> : <span className="caret-4-desc" />;
  };

  return (
    <>
      <SimpleHeader name={t("sideNav.verificationQueue", "Verification Queue")} parentName={t("sideNav.moderation", "Moderation")} />
      
      <Container className="mt--6" fluid>
        <Card className="shadow border-0">
          <div className="pt-4 pb-0">
            {/* Table Search Header */}
            <div className="d-flex flex-column flex-md-row pb-4 justify-content-between align-items-start align-items-md-center px-4">
              <div className="dataTables_filter d-flex align-items-center mb-2 mb-md-0">
                <label className="d-flex align-items-center mb-0">
                  <span className="text-sm font-medium text-slate-600 mr-2">
                    {t("table.search", "Search")}:
                  </span>
                  <div className="position-relative d-flex align-items-center" style={{ width: "100%", maxWidth: "220px" }}>
                    <Input
                      className="form-control-sm"
                      placeholder={t("categories.moderation.searchUser", "Search by user...")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ paddingRight: "24px" }}
                    />
                    {search && (
                      <span
                        onClick={() => setSearch("")}
                        className="position-absolute cursor-pointer search-clear-btn"
                        style={{ right: "8px", top: "50%", transform: "translateY(-50%)", zIndex: 5 }}
                      >
                        <CloseIcon style={{ fontSize: "14px" }} />
                      </span>
                    )}
                  </div>
                </label>
              </div>
              <Button size="sm" color="neutral" onClick={refresh} className="mr-4">
                <i className="fa fa-sync mr-1" />
                {t("common.refresh", "Refresh Queue")}
              </Button>
            </div>
          </div>

          {/* Main Table */}
          {error && (
            <div className="px-4 pb-4">
              <Alert color="danger" className="mb-0">{error}</Alert>
            </div>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table align-items-center table-flush mb-0">
                <thead className="thead-light">
                  <tr>
                    <th onClick={() => handleSort("userId")} className="cursor-pointer sortable">
                      {t("table.headers.user", "User")} {renderSortIcon("userId")}
                    </th>
                    <th onClick={() => handleSort("contentCategory")} className="cursor-pointer sortable">
                      {t("table.headers.category", "Category")} {renderSortIcon("contentCategory")}
                    </th>
                    <th onClick={() => handleSort("attemptsCount")} className="cursor-pointer sortable text-center">
                      {t("table.headers.attemptsCount", "Attempts")} {renderSortIcon("attemptsCount")}
                    </th>
                    <th className="text-center">
                      {t("table.headers.verificationStatus", "Verification Status")}
                    </th>
                    <th onClick={() => handleSort("fixDeadline")} className="cursor-pointer sortable">
                      {t("table.headers.daysLeft", "Days Left")} {renderSortIcon("fixDeadline")}
                    </th>
                    <th>{t("table.headers.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted">
                        {t("page.flagged.noContent", "No verification items found.")}
                      </td>
                    </tr>
                  ) : (
                    data.map((row) => {
                      const userObj = row.userId || {};
                      const userName = userObj ? `${userObj.firstName || ""} ${userObj.lastName || ""}` : t("categories.moderation.unknown", "Unknown User");
                      const userPic = userObj?.profilePictureUrl
                        ? `${constants.s3Bucket}/profile-pics/${userObj.profilePictureUrl}`
                        : null;

                      // Days Left
                      let daysLeftBadge = "-";
                      if (row.fixDeadline) {
                        const deadline = new Date(row.fixDeadline);
                        const today = new Date();
                        const diffTime = deadline - today;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        if (diffDays < 0) {
                          daysLeftBadge = (
                            <Badge color="danger" className="badge-pill">
                              ⚠️ {t("categories.moderation.overdue", "Overdue")}
                            </Badge>
                          );
                        } else {
                          daysLeftBadge = (
                            <Badge color={diffDays <= 3 ? "warning" : "info"} className="badge-pill">
                              {diffDays} {diffDays === 1 ? t("categories.moderation.day", "day") : t("categories.moderation.days", "days")}
                            </Badge>
                          );
                        }
                      }

                      const highlighted = isHighlighted("verification", row._id) || (row.contentId && isHighlighted("listing", row.contentId));

                      return (
                        <tr key={row._id} className={highlighted ? "border-left-highlight" : ""}>
                          <td>
                            <div className="d-flex align-items-center">
                              {userPic ? (
                                <Avatar src={userPic} sx={{ width: 32, height: 32 }} />
                              ) : (
                                <Avatar
                                  {...helpers.stringAvatar(userName)}
                                  sx={{ width: 32, height: 32, fontSize: 14 }}
                                />
                              )}
                              <div className="ml-2">
                                <span className="font-weight-bold d-block">{userName}</span>
                                <small className="text-muted">{userObj.email}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            {t(`page.flagged.categories.${row.contentCategory}`, row.contentCategory)}
                          </td>
                          <td className="text-center">
                            <Badge color="secondary" className="badge-pill font-weight-bold" style={{ fontSize: "11px" }}>
                              {row.attemptsCount || 1}
                            </Badge>
                          </td>
                          <td className="text-center">
                            {userObj?.verificationStatus ? (
                              <Badge
                                color={
                                  userObj.verificationStatus === "approved"
                                    ? "success"
                                    : userObj.verificationStatus === "rejected"
                                    ? "danger"
                                    : "warning"
                                }
                                className="badge-pill text-uppercase"
                                style={{ fontSize: "10px" }}
                              >
                                {userObj.verificationStatus}
                              </Badge>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>{daysLeftBadge}</td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <Button
                                id={`details-${row._id}`}
                                size="sm"
                                onClick={() => navigate(`/admin/moderation/verification/${row._id}/details`)}
                              >
                                <VisibilityIcon fontSize="small" color="primary" />
                              </Button>
                              <Button
                                id={`message-${row._id}`}
                                size="sm"
                                onClick={() => toggleMessageModal(row)}
                              >
                                <ChatIcon fontSize="small" color="info" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {!loading && data.length > 0 && (
            <div className="py-3 px-4 border-top">
              <div className="pagination-override-margin">
                <style>{`
                  .pagination-override-margin .pagination-container {
                    margin-bottom: 0px !important;
                    margin-top: 0px !important;
                  }
                `}</style>
                <Pagination
                  totalRows={totalItems}
                  rowsPerPage={limit}
                  currentPage={page - 1}
                  onPageChange={(p) => handlePageChange(p + 1)}
                  onRowsPerPageChange={(l) => handleLimitChange(l)}
                  t={t}
                />
              </div>
            </div>
          )}
        </Card>
      </Container>

      {/* Dismiss Flag Modal */}
      <DeleteModal
        isOpen={approveModalOpen}
        toggle={() => toggleApproveModal()}
        itemName="flagged"
        onDelete={handleApprove}
        loading={actionLoading}
      />

      {/* Send Message Modal */}
      <Modal isOpen={messageModalOpen} toggle={() => toggleMessageModal()} size="md" className="modal-dialog-centered">
        <ModalHeader className="border" toggle={() => toggleMessageModal()}>
          {t("categories.moderation.verification.sendGuidance", "Send Guidance / Suggestion")}
        </ModalHeader>
        <ModalBody className="px-4 py-3">
          <FormGroup>
            <Label for="messageText">{t("categories.moderation.messageLabel", "Message content")}</Label>
            <Input
              type="textarea"
              id="messageText"
              rows="4"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder={t("categories.moderation.verification.guidancePlaceholder", "Provide clear instructions or request specific changes...")}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter className="border">
          <Button color="secondary" onClick={() => toggleMessageModal()} disabled={actionLoading}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            color="primary"
            onClick={handleSendMessage}
            disabled={messageText.trim().length === 0 || actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : t("categories.moderation.sendMessageConfirm", "Send Warning")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default VerificationQueue;
